import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, UrlTree } from '@angular/router';
import { catchError, finalize, map, Observable, of } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { LoadingService } from '../services/loading.service';
import { SafeAny } from '../types/safe-any';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  isAccessible: boolean = false;

  constructor(
    private readonly authService: AuthService,
    private readonly loadingService: LoadingService
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    this.loadingService.setLoading(true);

    return this.authService.isAuthenticated().pipe(
      map((isAuthenticated) => {
        if (!isAuthenticated) {
          this.authService.forceLogout();
          return false;
        }
        return true;
      }),
      catchError(() => {
        this.authService.forceLogout();
        return of(false);
      }),
      finalize(() => this.loadingService.setLoading(false))
    );
  }

  filterMenuByPermissions(menus: SafeAny[], permissions: SafeAny[], currentPath: string): SafeAny[] {
    return menus.reduce((acc, menu) => {
      const sectionKey = menu.key;
      const permissionSection = permissions[sectionKey];

      if (!permissionSection || permissionSection.enabled === false) {
        return acc;
      }

      const filteredMenu = { ...menu };

      if (menu.childs) {
        filteredMenu.childs = menu.childs.reduce((childAcc: SafeAny[], child: SafeAny) => {
          const labelKey = child.key;

          if (child.path) {
            if (permissionSection[labelKey] === true) {
              childAcc.push(child);
              if (currentPath === child.path) this.isAccessible = true;
            }
          } else if (child.grandChilds) {
            const subKey = child.key;
            const subPermissions = permissionSection[subKey];

            if (subPermissions && subPermissions.enabled !== false) {
              const filteredGrandChilds = child.grandChilds.filter(
                (grandChild: { label: string; key: string }) => {
                  const grandKey = grandChild.key;
                  const isTrue = subPermissions[grandKey] === true;
                  if (currentPath === child.path && isTrue) this.isAccessible = true;
                  return isTrue;
                }
              );

              if (filteredGrandChilds.length > 0) {
                childAcc.push({ ...child, grandChilds: filteredGrandChilds });
              }
            }
          }

          return childAcc;
        }, []);
      } else if (permissionSection) {
          acc.push(filteredMenu);
      }

      if (filteredMenu.childs && filteredMenu.childs.length > 0) {
        acc.push(filteredMenu);
      }

      return acc;
    }, [] as SafeAny[]);
  }
}
